import { useState, useCallback } from 'preact/hooks';
import { pdpaService } from '../services/pdpaService';

/**
 * Custom hook for managing PDPA consent workflow
 * Handles policy fetching, consent state management, and submission
 */
export const usePdpa = () => {
  const [policy, setPolicy] = useState(null);
  const [consentStatus, setConsentStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch the current PDPA policy version
   * Should be called on component mount before showing consent form
   */
  const fetchPolicy = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const policyData = await pdpaService.getCurrentPolicy();
      setPolicy(policyData);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'ไม่สามารถโหลดนโยบายได้ กรุณารีเฟรชหน้าเว็บ';
      setError(message);
      console.error('PDPA fetchPolicy error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Toggle single PDPA checkbox state
   * @param {boolean} accepted
   */
  const toggleConsent = useCallback((accepted) => {
    setConsentStatus(accepted ? 'ACCEPTED' : 'WITHDRAWN');
  }, []);

  /**
   * Check if all required consents are accepted
   * @returns {boolean}
   */
  const isConsentAccepted = useCallback(() => {
    return consentStatus === 'ACCEPTED';
  }, [consentStatus]);

  /**
   * Submit consolidated consent to backend
   * @param {string} customerId - Customer UUID
   * @param {string} phoneNumber - Optional phone number
   * @throws {Error} If submission fails
   */
  const submitConsentLog = useCallback(
    async (customerId, phoneNumber) => {
      if (!policy) {
        throw new Error('Policy not loaded. Call fetchPolicy first.');
      }

      if (!consentStatus) {
        throw new Error('Consent status not selected.');
      }

      setLoading(true);
      setError(null);

      try {
        const payload = {
          customerId,
          phoneNumber,
          consentStatus,
          policyVersion: policy.policyVersion,
        };

        const response = await pdpaService.submitConsent(payload);

        if (!response.success) {
          throw new Error(response.message || 'Failed to submit consents');
        }

        return response;
      } catch (err) {
        const message =
          err?.response?.data?.message || err?.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [policy, consentStatus],
  );

  return {
    policy,
    consentStatus,
    loading,
    error,
    fetchPolicy,
    toggleConsent,
    submitConsentLog,
    isConsentAccepted,
  };
};
